import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { toastr } from 'react-redux-toastr'
import { connect } from 'react-redux'
import _ from 'lodash'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import { RiAlertFill } from 'react-icons/ri'

import AvailableDatesNav from '../DailyEvents/AvailableDatesNav'
import TripReplayControls from './TripReplayControls'
import SpeedEventsDropdown from './SpeedEventsDropdown'

import { vehicleSidebarIcons } from '../../../component/helpers/iconHelpers'
import momenttz from '../../../component/helpers/momenttz'
import { formatAddress } from '../../../component/helpers/tablesFuncHelpers'
import {
	getDeviceRoutes,
	getDeviceRoutesDates,
	getMissingGps
} from '../../../action/DeviceActions';
import { getEquipmentDates, getEquipmentMetaData } from '../../../action/EquipmentActions';
import { getCompanyDrivers, getUnknownDriversTrip } from '../../../action/CompaniesActions';
import ModalSelectNewDriver from '../../../component/smart/modals/ModalSelectNewDriver';
import CustomButton from '../../../component/CustomButton'

import './TripsGrid.sass'
import { isMobileOnly } from "react-device-detect";
import TripSnapshots from "../TrackTrace/TripSnapshotsHM";

const TripsGrid = (props) => {
	const { map, user, company, eventDate, activeTreeItem, selectedTrip, setPropStates, onClickPlayback, deviceTrips, toggleLoader, dashboardRedirect, setSelectedTrip, setTripsGPS, setShowTripsHistory, setSelectedDropdownEventId, equipmentMetadata, setEquipmentMetadata, zoomToVehicle } = props;
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'homePage.vehicleSidebar.tripsGrid' });
	const prevPropsActiveTreeItemRef = useRef(props.activeTreeItem);
	const tripReplayControlsRef = useRef(null);
	const isUsingDriverFaceID = company.driver_recognition === "faceid";

	const [availableDates, setAvailableDates] = useState([]);
	const [selectedTripState, setSelectedTripState] = useState('');
	const [speedUnit, setSpeedUnit] = useState(user.speed_unit);
	const [autoplayEnabled, setAutoplayEnabled] = useState(false);
	const [loopCount, setLoopCount] = useState(0);
	const [drivers, setDrivers] = useState([]);
	const [detectDriver, setDetectDriver] = useState(null);
	const isDesktop = !(isMobileOnly || window.innerWidth <= 991)

	const getParsedMetadata = (items) => {
		const metadata = {};
		if (items.length) {
			metadata.batteryLevel = items[0].battery_level;
			metadata.lastPing = moment(items[0].created_at, "YYYY-MM-DD HH:mm:ss").fromNow();
			metadata.movements = items.map(item => ({
				beacon_id: item.beacon_id,
				address: item.address,
				time: moment(item.created_at, "YYYY-MM-DD HH:mm:ss").format("h:mm a"),
			}))
		}
		return metadata;
	}

	useEffect(() => {
		getAvailableDates();

		return () => {
			setSelectedTrip(null)
			setTripsGPS([])
			setShowTripsHistory(false)
			setSelectedDropdownEventId(null)
		}
	}, [])

	useEffect(() => {
		if (!isDesktop && !selectedTrip) {
			setSelectedTripState('')
		}
	}, [selectedTrip])

	useEffect(() => {
		if (availableDates.length) {
			if (activeTreeItem.vehiclesType === 'equipment') {
				const date = availableDates[availableDates.length - 1];
				const formattedDate = moment(date, "YYYY-MM-DD").format("YYYYMMDD");
				setPropStates({ eventDate: availableDates[availableDates.length - 1] });
				getEquipmentRoutes(formattedDate);
			} else {
				// FWSD-4405
				if (dashboardRedirect && ['redirectFromUnknownDrivers', 'redirectFromDailyEvents'].includes(dashboardRedirect.type)) {
					const newEventDate = dashboardRedirect.timestamp
					setPropStates({ eventDate: momenttz(newEventDate + '+00') })
					props.setDashboardRedirect(null) // clears the dashboardRedirect redux props
					getDeviceTrips(
						1, 10, true, momenttz(newEventDate + '+00'),
						(deviceTrips) => {
							handleTripCardClick(findTripByTime(newEventDate, deviceTrips))
						},
					)
				} else {
					setPropStates({ eventDate: availableDates[availableDates.length - 1] })
					getDeviceTrips(1, 10, true, availableDates[availableDates.length - 1])
				}
			}
		}
	}, [availableDates])

	useEffect(() => {
		if (prevPropsActiveTreeItemRef.current.device_id !== activeTreeItem.device_id) {
			if (activeTreeItem.vehiclesType === 'equipment') {
				getAvailableDates();
			}
			setShowTripsHistory(false)
		}
		prevPropsActiveTreeItemRef.current = activeTreeItem
	}, [activeTreeItem.device_id]);

	useEffect(() => {
		if (isUsingDriverFaceID && company.company_id) {
			getCompanyDrivers({ companyId: company.company_id, include_data: 'photos,legal_consent' })
				.then(res => {
					setDrivers(_.get(res, 'data.response.drivers', []));
				})
				.catch((error) => {
					console.log('getDriversError: ', error);
				});
		}
	}, [company.company_id]);

	const handleDetectDriverClick = (e, trip) => {
		e.stopPropagation();
		const tripTimestamp = momenttz(`${trip.start_gps.timestamp}+00`);
		const startTs = tripTimestamp.format('YYYYMMDD000000');
		const endTs = tripTimestamp.format('YYYYMMDD235959');
		const baseRow = {
			id: trip.route_id,
			vehicle_name: activeTreeItem.vehicle_name || activeTreeItem.device_id,
			timestamp: tripTimestamp.format('YYYY-MM-DD HH:mm:ss'),
		};
		toggleLoader(true);
		getUnknownDriversTrip({
			companyId: company.company_id,
			start_ts: startTs,
			end_ts: endTs,
			include_data: 'driver_photos',
		})
			.then(res => {
				toggleLoader(false);
				const trips = _.get(res, 'data.response', []);
				const matched = trips.find(t => t.id === trip.route_id);
				setDetectDriver({
					...baseRow,
					photos: _.get(matched, 'photos', []),
				});
			})
			.catch(error => {
				toggleLoader(false);
				console.log('getUnknownDriversTrip error: ', error);
				setDetectDriver({ ...baseRow, photos: [] });
			});
	};

	const getEquipmentRoutes = (formattedDate) => {
		getEquipmentMetaData({
			beacon_id: activeTreeItem.device_id,
			date: formattedDate,
		}).then(res => {
			const metadataItems = res.data.response.metadata;
			const sortedItems = metadataItems.sort((a, b) => {
				return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
			});
			const parsedMetadata = getParsedMetadata(sortedItems);
			setEquipmentMetadata(parsedMetadata);
		}).catch((error) => {
			setEquipmentMetadata({});
			console.log('!getEquipmentMetaData error: ', error.response, error);
		});
	};

	const getAvailableDates = (callback) => {
		if (activeTreeItem.vehiclesType === 'equipment') {
			setEquipmentMetadata({});
			getEquipmentDates(activeTreeItem.device_id).then(res => {
				const dates = res.data.response;
				setAvailableDates(dates);
			}).catch((error) => {
				console.log('!getAvailableDates error: ', error.response, error);
			});
		} else {
			const tz = moment().utcOffset();
			const timezone = 'UTC' + ((tz > 0) ? '+' : '') + tz / 60
			const params = {
				start_ts: '20150101000000',
				end_ts: moment.utc().format('YYYYMMDDHHmmss'),
				timezone,
			};
			getDeviceRoutesDates(activeTreeItem.device_id, params).then((res) => {
				setAvailableDates(res.data.response.events_dates);

				if (callback && typeof callback === 'function') {
					callback();
				}
			}).catch((error) => {
				console.log('!getAvailableDates error: ', error.response, error);

				let errDescription = i18n.t('errorMessage.errDescription');
				if (error.response.data.response.error) {
					errDescription = error.response.data.response.error
				}
				toastr.error(errDescription)
			});
		}
	}

	const getDeviceTrips = (page = 1, sizePerPage = 10, showLoader = true, argEventDate = null, callback = null) => {
		const { eventDate, activeTreeItem, setDeviceTrips } = props
		const paramEventDate = argEventDate || eventDate
		toggleLoader(true)
		const params = {
			start_ts: momenttz(moment(paramEventDate).format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss'),
			end_ts: momenttz(moment(paramEventDate).format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss'),
		}
		getDeviceRoutes(activeTreeItem.device_id, params).then((res) => {
			const { routes } = res.data.response
			const routesSorted = routes.sort((a, b) => ((!a.end_gps || !a.end_gps.timestamp || (a.end_gps.timestamp > b.end_gps.timestamp)) ? -1 : 1))
			const newDeviceTrips = routesSorted.map((item) => {
				if (!item.end_gps) {
					item.end_gps = { timestamp: momenttz().utc().format('YYYY-MM-DD HH:mm:ss') }
					item.duration = moment.utc().diff(moment.utc(item.start_gps.timestamp), 'seconds')
					item.isLiveTrip = true
				} else if (!item.end_gps.timestamp) {
					item.end_gps.timestamp = momenttz().utc().format('YYYY-MM-DD HH:mm:ss')
					item.duration = moment.utc().diff(moment.utc(item.start_gps.timestamp), 'seconds')
					item.isLiveTrip = true
				}
				return item
			})
			setDeviceTrips(newDeviceTrips)
			if (typeof callback === 'function') {
				callback(newDeviceTrips)
			}

			toggleLoader(false);
		}).catch((error) => {
			toggleLoader(false);
			console.log('!getDeviceTrips error: ', error.response, error)

			let errDescription = i18n.t('errorMessage.errDescription');
			if (error.response.data.response.error) {
				errDescription = error.response.data.response.error
			}
			toastr.error(errDescription)
		})
	}

	const handleTripCardClick = (argTrip, isAutoplay) => {
		const { map, activeTreeItem, setTripsGPS, setSelectedTrip } = props

		if (!argTrip || (isDesktop && selectedTripState === argTrip.route_id)) {
			return
		}

		if (!isAutoplay) {
			setLoopCount(0);
			setAutoplayEnabled(false);
		}

		setSelectedTripState(argTrip.route_id)
		setSelectedTrip(argTrip)

		const params = {
			device_ids: activeTreeItem.device_id,
			start_ts: momenttz(`${argTrip.start_gps.timestamp}+00`).utc().format('YYYYMMDDHHmmss'),
			end_ts: momenttz(`${argTrip.end_gps.timestamp}+00`).utc().format('YYYYMMDDHHmmss'),
		}
		toggleLoader(true)
		getMissingGps(params).then((res, err) => {
			const polygons = []
			const polygon = []
			const { response } = res.data
			response.map((vehicle) => {
				vehicle.metadata.map((item, index) => {
					if (item.latitude) {
						polygon.push({
							lat: item.latitude,
							lng: item.longitude,
							speed: item.speed,
							timestamp: moment.utc(item.timestamp).format('YYYY-MM-DD HH:mm:ss+00'),
							dir: item.dir,
							key: item.key,
							driver_id: index,
							driver_name: `deiver ${index}`,
							dst: item.dst,
							type: item.type,
						})
					}
				})
			});

			polygons.push({
				polygon,
				starttimestamp: argTrip.start_gps.timestamp,
				endtimestamp: argTrip.end_gps.timestamp,
			});

			setTripsGPS(polygons);

			// map zooming: the map should adjust it's zoom level to encompass the entire trip
			if (polygon.length) {
				const latStart = polygon[0].lat;
				const latEnd = polygon[polygon.length - 1].lat;
				let top = 50;
				let bottom = 0;

				if (latStart > latEnd) {
					top = 250;
					bottom = 50;
				}

				map.getViewPort().setPadding(top, 50, bottom, 50);
				map.getViewModel().setLookAtData({ bounds: map.getObjects().filter(e => e && e instanceof H.map.Polyline)[0]?.getBoundingBox() });
			}
			toggleLoader(false);
		}).catch((error) => {
			toggleLoader(false);
			console.log('!handleTripCardClick error:', error.response, error)

			toastr.error(t('toastrError'))
		});
	}

	const handleAlertDropdownClick = (event) => {
		const { map, tripsEvents, setSelectedDropdownEventId, setTripTimelineTimestamp } = props

		const obj = tripsEvents.find(({ id }) => id * 1 === event.id * 1)
		if (obj) {
			map.setCenter(obj.position)
			map.setZoom(18, true)
			setSelectedDropdownEventId(event.id)
			setTripTimelineTimestamp(momenttz(`${event.timestamp + '+00'}`).format('YYYY-MM-DD HH:mm:ss'))
		}
	}

	const findTripByTime = (argTimestamp, argDeviceTrips = null) => {
		const varDeviceTrips = argDeviceTrips || deviceTrips
		const obj = varDeviceTrips.find((trip) => trip.start_gps.timestamp <= argTimestamp)

		if (obj) {
			return obj
		}
		return varDeviceTrips[varDeviceTrips.length - 1]
	}

	const totalDistance = deviceTrips.reduce((acc, curr) => acc + curr.total_distance, 0);

	const handleTripEnd = (endedTrip) => {
		if (!autoplayEnabled) return;

		const currentIndex = deviceTrips.findIndex(t => t.route_id === endedTrip.route_id);
		const nextIndex = currentIndex - 1;

		if (nextIndex >= 0) {
			const nextTrip = deviceTrips[nextIndex];
			handleTripCardClick(nextTrip, true);

			setTimeout(() => {
				onClickPlayback(momenttz(nextTrip.start_gps.timestamp + '+00'));
				if (tripReplayControlsRef.current) {
					tripReplayControlsRef.current.playStopPlayback();
				}
			}, 2000);

		} else {
			if (loopCount < 2) {
				const loopTrip = deviceTrips[deviceTrips.length - 1];
				handleTripCardClick(loopTrip, true);

				setTimeout(() => {
					onClickPlayback(momenttz(loopTrip.start_gps.timestamp + '+00'));
					if (tripReplayControlsRef.current) {
						tripReplayControlsRef.current.playStopPlayback();
					}
				}, 2000);

				setLoopCount(loopCount + 1);
			} else {
				setAutoplayEnabled(false);
				setLoopCount(0);
			}
		}
	};


	return (
		<div className="tripsGridWrapper">
			{(_.isEmpty(selectedTrip) || isDesktop) ? (
				<div className="date date-container-mobile">
					<AvailableDatesNav
						eventDate={eventDate}
						availableDates={availableDates}
						sizePerPage={1}
						setPropStates={setPropStates}
						getFleetEvents={getDeviceTrips}
						vehiclesType={activeTreeItem.vehiclesType}
						getEquipmentRoutes={getEquipmentRoutes}
						isTripHistory
						clearTimer={() => { }}
					/>
				</div>
			) : ''}

			{activeTreeItem.vehiclesType === 'equipment' ? (
				!_.isEmpty(equipmentMetadata) ? (
					<>
						<div className="last-ping">
							<label>{t('lastPing')}:</label>&nbsp;
							<span>{equipmentMetadata.lastPing}</span>
						</div>
						<div className="equipment-cards">
							{equipmentMetadata.movements.length ? (
								equipmentMetadata.movements.map((item, index) => (
									<div
										key={`equipmentRouteCard_${index}`}
										className="equipment-trip-card"
										onClick={() => {
											if (zoomToVehicle) {
												zoomToVehicle(15);
											}
										}}
									>
										<div className="trip-row">
											<span className="row-address">{item.address}</span>
											<span className="row-time">{item.time}</span>
										</div>
									</div>
								))
							) : ''}
						</div>
					</>
				) : ''
			) : (
				<>
					{(_.isEmpty(selectedTrip) || isDesktop) ? (
						<>
							<div className="totalDistance">
								<div>
									<label>{t('totalDistance')}</label>
									<span>{(totalDistance * (speedUnit === 'kph' ? 1 : 0.62)).toFixed(1)} {(speedUnit === 'kph' ? ' Km' : ' Mi')}</span>
								</div>
								<div className='toggle-buttons button-group'>
									<CustomButton
										variant='toggle'
										active={speedUnit === 'mph'}
										onClick={() => setSpeedUnit('mph')}
									>
										MPH
									</CustomButton>
									<CustomButton
										variant='toggle'
										active={speedUnit === 'kph'}
										onClick={() => setSpeedUnit('kph')}
									>
										KPH
									</CustomButton>
								</div>
							</div>
							<div className="separator"/>
							<div className="tripCardsWrapper">
								{deviceTrips.length ?
									deviceTrips.map((item, index) => {
										const liveTrip = item.isLiveTrip // FWSD-3740

										return (
											<div
												className={`
											tripCard
											${!_.isEmpty(selectedTrip) && selectedTrip.start_gps.timestamp === item.start_gps.timestamp ? 'active' : ''}
											${liveTrip ? 'liveTrip' : ''}
										`}
												key={item.route_id}
												role="button"
												tabIndex="0"
												onClick={() => handleTripCardClick(item)}
											>
												{liveTrip ? (
													<div className="tripEnd">
														<b>{t('tripEnd')}</b>
													</div>
												) : (
													<div className="tripEnd">
														<div className="iconWrapper">
															<img src="/images/ico/marker_stop.svg" alt={t('tripStop')}/>
														</div>
														<div className="address">
															{formatAddress(item.end_address)}
														</div>
														<div className="timestamp">
															{momenttz(`${item.end_gps.timestamp}+00`).format('h:mm a')}
														</div>
													</div>
												)}
												<div className="tripDuration">
													{liveTrip ? (
														<div className="content" style={{ paddingLeft: 0 }}>
													<span style={{ marginRight: '20px' }}>
														{moment.duration(moment.utc().diff(moment.utc(item.start_gps.timestamp), 'seconds'), 'seconds').format('h [hr] m [min] s [sec]', { trim: 'both' })}
													</span>
														</div>
													) : (
														<div className="content">
													<span style={{ marginRight: '20px' }}>
														{moment.duration(item.duration, 'seconds').format('h [hr] m [min] s [sec]', { trim: 'both' })}
													</span>
															<span style={{ marginRight: '5px' }}>
														{vehicleSidebarIcons.tripDuration}
													</span>
															<span style={{ marginRight: '3px' }}>
														{(item.total_distance * ((user.speed_unit === 'kph') ? 1 : 0.62)).toFixed(1)}
													</span>
															{user.speed_unit === 'kph' ? 'Km' : 'Mi'}
														</div>
													)}
												</div>
												<div
													className="tripDriver">Driver: {item.driver_name ? item.driver_name : 'Unknown'}
													{isUsingDriverFaceID && !item.driver_name && (
														<CustomButton
															variant="link"
															onClick={(e) => handleDetectDriverClick(e, item)}
														>
															{t('detectDriver')}
														</CustomButton>
													)}
												</div>
												<div className="tripStart">
													<div className="iconWrapper" style={{ top: 0 }}>
														<img src="/images/ico/marker_start.svg" alt={t('tripStart')}/>
													</div>
													<div className="address">
														{formatAddress(item.start_address)}
													</div>
													<div className="timestamp">
														{momenttz(`${item.start_gps.timestamp}+00`).format('h:mm a')}
													</div>
												</div>
												<div className="alertsWrapper">
													{item.isTripProcessing && (
														<div className="alertBlock">
															{/* <span className="glyphicon glyphicon-alert trip-processing" /> */}
															<RiAlertFill className="glyphicon-alert"/>
															<span className="value">{t('tripIsProcessing')}</span>
														</div>
													)}
													{item.events && item.events.length > 0
														&& (
															<Dropdown
																drop
																className="alertDropdown"
																id="trip-events-dropdown"
															>
																<Dropdown.Toggle
																	className="alertDropdownToggle no-caret">
																	<div className="alertBlock">
																		{/* <span className="glyphicon glyphicon-alert" /> */}
																		<RiAlertFill className="glyphicon-alert"/>
																		<span
																			className="value">{item.events.length}</span>
																	</div>
																</Dropdown.Toggle>
																<Dropdown.Menu className="alertDropdownMenu" drop>
																	{item.events.map((event) => {
																		const event_type = event.event_type ? i18n.t(`constants.eventNames.${event.event_type.replace(/\s/g, "_")}`, event.event_type) : 'N/A';
																		return (
																			<Dropdown.Item
																				key={event.id}
																				onClick={() => handleAlertDropdownClick(event)}
																			>
																				{event.timestamp ? momenttz(`${event.timestamp}+00`).format('h:mm:ss a') : 'N/A'} - {event_type}
																			</Dropdown.Item>
																		)
																	})}
																</Dropdown.Menu>
															</Dropdown>
														)}

													<SpeedEventsDropdown speedingEvents={item.speeding_events}/>
												</div>
												{['system_admin', 'customer_service'].includes(user.roles[0]) && item.time_idling &&
													<span style={{
														display: 'inline-block',
														marginTop: '5px'
													}}>{t('timeIdling') + item.time_idling}</span>
												}

												{index !== (deviceTrips.length - 1) &&
													<div className="prevTrip">
														<div className="value">
															{item.time_from_prev_trip > 3600 ?
																moment.duration(item.time_from_prev_trip, 'seconds').format('h[h] m[m]', { trim: 'both' }) :
																moment.duration(item.time_from_prev_trip, 'seconds').format('m[m] s[s]', { trim: 'both' })
															}
														</div>
													</div>
												}
											</div>
										)
									}) :
									<div className='no-trips-message'>{t('noTripsAvailable')}</div>
								}
							</div>
						</>
					) : ''}


					{(!_.isEmpty(selectedTrip) || isDesktop) ? (
						<>
							<TripReplayControls
								ref={tripReplayControlsRef}
								activeTreeItem={activeTreeItem}
								onClickPlayback={onClickPlayback}
								onTripEnd={handleTripEnd}
								autoplayEnabled={autoplayEnabled}
								setAutoplayEnabled={setAutoplayEnabled}
							/>
							{selectedTrip && <TripSnapshots map={map} activeTreeItem={activeTreeItem} />}
						</>
					) : ''}
				</>
			)}
			{detectDriver && (
				<ModalSelectNewDriver
					drivers={drivers}
					selectedRow={detectDriver}
					isUsingDriverFaceID={true}
					getDeviceTrips={getDeviceTrips}
					onHide={() => setDetectDriver(null)}
				/>
			)}
		</div>
	)
}

TripsGrid.propTypes = {
	map: PropTypes.objectOf(PropTypes.any).isRequired,
	user: PropTypes.objectOf(PropTypes.any).isRequired,
	activeTreeItem: PropTypes.objectOf(PropTypes.any).isRequired,
	selectedTrip: PropTypes.objectOf(PropTypes.any),
	setPropStates: PropTypes.func.isRequired,
	setTripsGPS: PropTypes.func.isRequired,
	setSelectedTrip: PropTypes.func.isRequired,
	onClickPlayback: PropTypes.func.isRequired,
	setDashboardRedirect: PropTypes.func.isRequired,
	deviceTrips: PropTypes.arrayOf(PropTypes.any).isRequired,
	dashboardRedirect: PropTypes.objectOf(PropTypes.any)
};

const mapStateToProps = ({ user, company, dashboardData }) => ({
	user: user.user,
	company: company.company,
	selectedTrip: dashboardData.selectedTrip,
	tripsGPS: dashboardData.tripsGPS,
	tripsEvents: dashboardData.tripsEvents,
	dashboardRedirect: dashboardData.dashboardRedirect,
	deviceTrips: dashboardData.deviceTrips
});

const mapDispatchToProps = dispatch => ({
	setTripsGPS: (state) => {
		dispatch({ type: 'SET_TRIPS_GPS', payload: state })
	},
	setSelectedTrip: (state) => {
		dispatch({ type: 'SET_SELECTED_TRIP', payload: state })
	},
	setShowTripsHistory: (state) => {
		dispatch({ type: 'SET_SHOW_TRIP_HISTORY', payload: state })
	},
	setSelectedDropdownEventId: (state) => {
		dispatch({ type: 'SET_SELECTED_DROPDOWN_EVENT_ID', payload: state })
	},
	setTripTimelineTimestamp: (state) => {
		dispatch({ type: 'SET_TRIP_TIMELINE_TIMESTAMP', payload: state })
	},
	setDashboardRedirect: (state) => {
		dispatch({ type: 'SET_DASHBOARD_REDIRECT', payload: state })
	},
	setDeviceTrips: (arr) => {
		dispatch({ type: 'SET_DEVICE_TRIPS', payload: arr })
	},
	toggleLoader: (show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}
})

export default connect(mapStateToProps, mapDispatchToProps)(TripsGrid);
